import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema } from './common';

/**
 * Canal por el que se emitió/envió una credencial QR.
 */
export const QrCanalEnvioSchema = z.enum(['email', 'descarga_pdf', 'vista_publica']);
export type QrCanalEnvio = z.infer<typeof QrCanalEnvioSchema>;

/**
 * Modelo del cuerpo del email de credencial.
 *
 * - `formal`: plantilla institucional completa (`renderPaseFormalHtml`) con
 *   cabecera UPTM, carné de credencial y **QR embebido** (data-URL), más el
 *   PDF formal adjunto. Es el default.
 * - `minimalista`: solo texto + botón + PDF adjunto, **sin imágenes embebidas**.
 *   Para clientes de correo que bloquean data-URLs (Gmail y otros clientes
 *   corporativos), donde el QR embebido puede quedar como un hueco vacío.
 *
 * En ambos casos el PDF formal va adjunto, así que la credencial siempre
 * llega aunque el cliente no renderice la imagen embebida.
 */
export const EmailPaseFormatoSchema = z.enum(['formal', 'minimalista']);
export type EmailPaseFormato = z.infer<typeof EmailPaseFormatoSchema>;

/**
 * Token QR (ver docs/00-compartido/esquemas-datos.md §8).
 * Tabla `qr_token`.
 */
export const QrTokenSchema = z.object({
  id: EntityIdSchema,
  participante_id: EntityIdSchema,
  jti: z.string().min(1),
  emitido_en: IsoDateTimeSchema,
  expira_en: IsoDateTimeSchema,
  revocado: z.union([z.literal(0), z.literal(1)]).or(z.boolean()),
  canal_envio: QrCanalEnvioSchema,
  payload_cifrado: z.string(),
  iv: z.string(),
});
export type QrToken = z.infer<typeof QrTokenSchema>;

/**
 * Token one-time para vista pública "consultar mi QR".
 * Tabla `credencial_token`.
 */
export const CredencialTokenSchema = z.object({
  id: EntityIdSchema,
  participante_id: EntityIdSchema,
  token_hash: z.string(),
  enviado_en: IsoDateTimeSchema,
  expira_en: IsoDateTimeSchema,
  usado_en: IsoDateTimeSchema.nullable().optional(),
  ip_solicitante: z.string().nullable().optional(),
  user_agent: z.string().nullable().optional(),
});
export type CredencialToken = z.infer<typeof CredencialTokenSchema>;

/**
 * Response de GET /api/v1/auth/verify-qr (público, one-time).
 */
export const VerifyQrResponseSchema = z.object({
  qr_png_base64: z.string(),
  pdf_url: z.string().url(),
});
export type VerifyQrResponse = z.infer<typeof VerifyQrResponseSchema>;

/**
 * Input para POST /api/v1/qr/generar.
 *
 * Sin `force`, si el participante ya tiene un QR vigente, se devuelve ese
 * mismo (mismo `jti` y misma `qr_png_base64`). Con `force: true` se revoca
 * el vigente y se emite uno nuevo (uso: compromiso de credencial).
 */
export const QrGenerarInputSchema = z.object({
  participante_id: z.string().min(1),
  force: z.boolean().optional(),
});
export type QrGenerarInput = z.infer<typeof QrGenerarInputSchema>;

/**
 * Response de POST /api/v1/qr/generar.
 */
export const QrGenerarResponseSchema = z.object({
  qr_png_base64: z.string(),
  expires_at: z.string(),
});
export type QrGenerarResponse = z.infer<typeof QrGenerarResponseSchema>;

/**
 * Input para POST /api/v1/cohortes/:id/credenciales/generar.
 * Genera credenciales para los participantes de la cohorte que aún no tengan
 * una vigente. Los que ya tienen vigente quedan como `estado: 'reusado'`
 * y no reciben email. Con `force: true` se revocan los vigentes y se emiten
 * nuevos (se reenvía email a los que tengan).
 */
export const CredencialesCohorteGenerarInputSchema = z.object({
  enviar_email: z.boolean().default(false),
  force: z.boolean().optional(),
  email_formato: EmailPaseFormatoSchema.optional(),
});
export type CredencialesCohorteGenerarInput = z.infer<typeof CredencialesCohorteGenerarInputSchema>;

/**
 * Estado del procesamiento de la credencial de un participante.
 * - generado:      QR emitido, sin envío por email solicitado.
 * - email_enviado: QR emitido y email enviado vía Resend.
 * - sin_email:     QR emitido, pero el participante no tiene email.
 * - reusado:       El participante ya tenía un QR vigente; no se rotó ni se
 *                  envió email. Reutilizar la credencial vigente es el camino
 *                  normal — solo se rota con `force: true` o revocación manual.
 * - error:         no se pudo emitir/enviar.
 */
export const CredencialCohorteEstadoSchema = z.enum([
  'generado',
  'email_enviado',
  'sin_email',
  'reusado',
  'error',
]);
export type CredencialCohorteEstado = z.infer<typeof CredencialCohorteEstadoSchema>;

export const CredencialCohorteItemSchema = z.object({
  participante_id: EntityIdSchema,
  nombre: z.string(),
  apellido: z.string(),
  jti: z.string(),
  expires_at: z.string(),
  estado: CredencialCohorteEstadoSchema,
  email: z.string().nullable(),
  error: z.string().nullable(),
});
export type CredencialCohorteItem = z.infer<typeof CredencialCohorteItemSchema>;

export const CredencialesCohorteResponseSchema = z.object({
  cohorte_id: EntityIdSchema,
  total: z.number().int(),
  emails_enviados: z.number().int(),
  emails_omitidos: z.number().int(),
  errores: z.number().int(),
  items: z.array(CredencialCohorteItemSchema),
});
export type CredencialesCohorteResponse = z.infer<typeof CredencialesCohorteResponseSchema>;

/**
 * Response de POST /api/v1/participantes/:id/credencial/enviar.
 * Envío unitario: emite un QR y lo envía por email a un único participante.
 * `verify_url` es el link público firmado al pase (null si no hay email).
 */
export const CredencialEnviarResponseSchema = CredencialCohorteItemSchema.extend({
  verify_url: z.string().nullable(),
});
export type CredencialEnviarResponse = z.infer<typeof CredencialEnviarResponseSchema>;

/**
 * Input opcional (body JSON) para POST /api/v1/participantes/:id/credencial/enviar.
 * `force: true` rota el QR vigente antes de reenviar el email.
 * `email_formato` elige el modelo del cuerpo del email (default: `formal`).
 */
export const CredencialEnviarInputSchema = z
  .object({
    force: z.boolean().optional(),
    email_formato: EmailPaseFormatoSchema.optional(),
  })
  .partial();
export type CredencialEnviarInput = z.infer<typeof CredencialEnviarInputSchema>;